import { FILTER_FEATURE } from "api/ApiPath";
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { FilterUseCase } from "app/filters/domain/useCase/FilterUseCase";
import { AirtimeManagementInjection } from "features/airTimeManagement/di/AirtimeManagementInjection";
import isUndefined from "lodash/isUndefined";
import { injectable } from "tsyringe";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { avoidDuplicate, paramsChecking } from "utils/helpersInTs/helpersInTs";
import { AirtimeActionEntity } from "../entities/AirtimeActionEntity";
import { AirtimeDetailEntity } from "../entities/AirtimeDetailEntity";
import { AirtimeRequestEntity } from "../entities/AirtimeRequestEntity";
import { AirtimeResponseEntity } from "../entities/AirtimeResponseEntity";
import type { IAirTimeManagementRepository } from "../repositories/IAirtimeManagementRepository";

@injectable()
export class AirtimeManagementUseCase {
  private airtimeRepository: IAirTimeManagementRepository;
  private filterUseCase: FilterUseCase;

  constructor() {
    this.airtimeRepository =
      AirtimeManagementInjection.getAirtimeManagementRepo();
    this.filterUseCase = FilterInjection.getUseCase();
  }

  public checkParams(value: AirtimeRequestEntity) {
    let params = {
      ...value,
      name: paramsChecking(value?.name, "input"),
      code: paramsChecking(value?.code, "input"),
      amount: paramsChecking(value?.amount?.toString(), "input"),
      carrier: paramsChecking(value?.carrier, "select"),
      type: paramsChecking(value?.type, "select"),
      status: value?.status === -1 ? undefined : value?.status,
    };

    return params;
  }

  private updateBody(
    body: AirtimeActionEntity,
    detailInfo: AirtimeDetailEntity,
  ) {
    const keysToAvoid = [
      "code",
      "carrier",
      "address",
      "displayName",
      "email",
      "phoneNumber",
    ];
    let renderedBody = Object.keys(body)?.filter(
      (item) =>
        !Array.isArray(body[item]) &&
        !keysToAvoid?.includes(item) &&
        !isUndefined(
          avoidDuplicate(detailInfo[item]?.toString(), body[item]?.toString()),
        ),
    );
    let afterBody = {
      code: body["code"],
      carrier: avoidDuplicate(detailInfo?.carrier?.id, body["carrier"]),
      ...renderedBody?.reduce(
        (a, v) => ({ ...a, [v]: body[v]?.toString() }),
        {},
      ),
    } as typeof body;

    return afterBody;
  }

  async getList(
    params: AirtimeRequestEntity,
  ): Promise<ListBaseApiResponseModel<AirtimeResponseEntity>> {
    const updateParams = this.checkParams(params);
    return await this.airtimeRepository.getList(updateParams);
  }

  async getDetail(params: {
    code: string;
  }): Promise<BaseApiResponseModel<AirtimeDetailEntity>> {
    return await this.airtimeRepository.getDetail(params);
  }

  async updateAirTime(
    body: AirtimeActionEntity,
    detailInfo: AirtimeDetailEntity,
  ): Promise<BaseApiResponseModel<AirtimeResponseEntity>> {
    const updateBody = this.updateBody(body, detailInfo);
    return await this.airtimeRepository.updateAirTime(updateBody);
  }

  async createAirTime(
    body: AirtimeActionEntity,
    detailInfo: AirtimeDetailEntity,
  ): Promise<BaseApiResponseModel<AirtimeResponseEntity>> {
    const updateBody = this.updateBody(body, detailInfo);
    return await this.airtimeRepository.createAirTime(updateBody);
  }

  async deleteAirTime(body: {
    code: string;
  }): Promise<BaseApiResponseModel<AirtimeResponseEntity>> {
    return await this.airtimeRepository.deleteAirTime(body);
  }

  async getCarrier(
    params: BaseFilterListRequestModel,
  ): Promise<Array<BaseFilterListResponseModel>> {
    return await this.filterUseCase.fetchListWithFeature({
      path: FILTER_FEATURE.CARRIER_LIST,
      params,
      isNeedFeature: true,
      savedKey: CacheKeyEnum.carrierList,
    });
  }
}
